import { DateTime, IANAZone } from 'luxon';

import { readCalendarDate } from './calendarDate';

/**
 * Moving between an instant and what a clock in a given IANA zone shows for it.
 *
 * THE ZONE IS ALWAYS AN ARGUMENT AND IS NEVER GUESSED. A missing or unknown zone throws rather than
 * falling back to the device's, because a fallback is a decision about whose zone applies, and that
 * belongs to the caller: Identity & Tenancy knows the user's and the clinic's zone, and the consuming
 * context decides which one a rule uses.
 */

/**
 * @param {*} value
 * @returns {boolean} true for a string naming an IANA time zone, e.g. 'America/Denver'
 */
export function isTimeZone(value) {
  return typeof value === 'string' && IANAZone.isValidZone(value);
} // isTimeZone()

const inZone = (instant, zone) => {
  if (!isTimeZone(zone)) {
    throw new RangeError(`Not an IANA time zone: ${zone}`);
  }
  if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
    throw new TypeError('Not a valid instant');
  }
  return DateTime.fromJSDate(instant, { zone });
};

/**
 * The calendar date a clock in `zone` shows at `instant`.
 *
 * @param {Date} instant
 * @param {string} zone - IANA zone
 * @returns {string} 'YYYY-MM-DD'
 */
export function toCalendarDate(instant, zone) {
  return inZone(instant, zone).toISODate();
} // toCalendarDate()

/**
 * The instant at which a clock in `zone` shows the given date and time of day.
 *
 * @param {string} calendarDate - 'YYYY-MM-DD'
 * @param {{hour?: number, minute?: number, second?: number}} wallClock - unset fields are 0
 * @param {string} zone - IANA zone
 * @returns {Date}
 */
export function atWallClock(calendarDate, { hour = 0, minute = 0, second = 0 }, zone) {
  if (readCalendarDate(calendarDate) === null) {
    throw new RangeError(`Not a calendar date: ${calendarDate}`);
  }
  if (!isTimeZone(zone)) {
    throw new RangeError(`Not an IANA time zone: ${zone}`);
  }
  return DateTime.fromISO(calendarDate, { zone })
    .set({ hour, minute, second, millisecond: 0 })
    .toJSDate();
} // atWallClock()

/**
 * The hour and minute a clock in `zone` shows at `instant`.
 *
 * @param {Date} instant
 * @param {string} zone - IANA zone
 * @returns {{hour: number, minute: number}}
 */
export function wallClockOf(instant, zone) {
  const { hour, minute } = inZone(instant, zone);

  return { hour, minute };
} // wallClockOf()

/**
 * `instant` with some of its calendar or clock fields replaced, as read in `zone`.
 *
 * @param {Date} instant
 * @param {Object} fields - Luxon unit names: year, month (1-based), day, hour, minute, second, millisecond
 * @param {string} zone - IANA zone
 * @returns {Date}
 */
export function adjustInZone(instant, fields, zone) {
  return inZone(instant, zone).set(fields).toJSDate();
} // adjustInZone()

/**
 * `instant` moved by a duration, where calendar units (days, months) follow the clock in `zone` across
 * a DST transition rather than adding a fixed number of hours.
 *
 * @param {Date} instant
 * @param {Object} duration - Luxon units: years, months, days, hours, minutes, seconds, milliseconds
 * @param {string} zone - IANA zone
 * @returns {Date}
 */
export function addInZone(instant, duration, zone) {
  return inZone(instant, zone).plus(duration).toJSDate();
} // addInZone()

// For `zonedFormat` only, so both modules refuse the same inputs. The barrel leaves it out: it hands
// back a Luxon object, and nothing outside the kernel should hold one.
export const toZonedDateTime = inZone;
